import { AlertCircle, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import type { User } from '@/types/user';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  getProfileReadiness,
  isProfileReadyToPublish,
  type MarketingAuthorizationReadiness,
  type PublicProfileCompanyCompliance
} from './profileReadiness';

interface ProfilePublicationPanelProps {
  profile: Partial<User>;
  published: boolean;
  updating: boolean;
  companyCompliance?: PublicProfileCompanyCompliance | null;
  hasUnsavedChanges?: boolean;
  marketingAuthorization?: MarketingAuthorizationReadiness | null;
  lockDescriptionId?: string;
  onPublicationChange: (enabled: boolean) => void;
}

export function ProfilePublicationPanel({
  profile,
  published,
  updating,
  companyCompliance,
  hasUnsavedChanges = false,
  marketingAuthorization,
  lockDescriptionId,
  onPublicationChange
}: ProfilePublicationPanelProps) {
  const readiness = getProfileReadiness(profile, companyCompliance, marketingAuthorization);
  const ready = isProfileReadyToPublish(profile, companyCompliance, marketingAuthorization);
  const completeCount = readiness.filter((item) => item.complete).length;

  return (
    <Card className="border-primary/20 bg-primary/[0.025]">
      <CardHeader className="gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle className="text-lg">Publication readiness</CardTitle>
            <Badge variant={published ? 'default' : 'secondary'}>
              {published ? 'Published' : 'Draft'}
            </Badge>
          </div>
          <CardDescription className="mt-2">
            {completeCount} of {readiness.length} profile checks complete. Optional sections remain hidden until populated.
          </CardDescription>
        </div>
        <Button
          type="button"
          variant={published ? 'outline' : 'default'}
          disabled={updating || hasUnsavedChanges || (!published && !ready)}
          aria-describedby={!published && !ready ? lockDescriptionId : undefined}
          onClick={() => onPublicationChange(!published)}
          className="min-w-32 gap-2"
        >
          {published ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
          {updating ? 'Updating…' : published ? 'Unpublish' : 'Publish profile'}
        </Button>
      </CardHeader>
      <CardContent>
        <div className="grid gap-2 sm:grid-cols-2">
          {readiness.map((item) => (
            <div key={item.id} className="flex items-start gap-3 rounded-lg border bg-background p-3">
              {item.complete ? (
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden="true" />
              ) : (
                <AlertCircle className={`mt-0.5 h-4 w-4 shrink-0 ${item.required ? 'text-warning' : 'text-muted-foreground'}`} aria-hidden="true" />
              )}
              <div>
                <p className="text-sm font-medium">
                  {item.label}{!item.required && <span className="ml-1 font-normal text-muted-foreground">Optional</span>}
                </p>
                {!item.complete && <p className="mt-1 text-xs text-muted-foreground">{item.guidance}</p>}
              </div>
            </div>
          ))}
        </div>
        {!ready && !published && (
          <p className="mt-4 text-sm text-muted-foreground">
            Complete every required check before publishing. Saving changes or reserving a URL will keep this profile in draft.
          </p>
        )}
        {hasUnsavedChanges && (
          <p className="mt-4 text-sm text-warning">
            Save the current profile changes before changing publication status.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
